import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Link,
  Chip,
  InputAdornment,
} from '@mui/material';
import { Refresh as RefreshIcon, Search as SearchIcon, Clear as ClearIcon, ArrowUpward as ArrowUpIcon, ArrowDownward as ArrowDownIcon, Circle as CircleIcon } from '@mui/icons-material';
import { AccountService } from '../../types/account';
import apiService from '../../services/api';

const SERVICE_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
  { value: '', label: 'All statuses' },
];

const getServiceTypeColor = (serviceType: string) => {
  switch (serviceType) {
    case 'nbn': return 'primary';
    case 'mobile': return 'secondary';
    case 'voice': return 'success';
    case 'sonweb': return 'info';
    case 'data': return 'warning';
    default: return 'default';
  }
};

type SortBy = 'service_id' | 'service_type' | 'account';
type SortOrder = 'asc' | 'desc';

const SESSION_STATUS_COLOR: Record<string, string> = {
  ONLINE: '#16a34a',
  STALE: '#d97706',
  OFFLINE: '#dc2626',
};

function SessionStatusIcon({ status }: { status?: string }) {
  if (!status) return <CircleIcon sx={{ fontSize: 12, color: 'text.disabled' }} />;
  const color = SESSION_STATUS_COLOR[status] ?? '#9ca3af';
  const label = status.charAt(0) + status.slice(1).toLowerCase();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <CircleIcon sx={{ fontSize: 12, color }} />
      <Typography variant="body2" sx={{ color }}>{label}</Typography>
    </Box>
  );
}

const ServicesReportPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [services, setServices] = useState<AccountService[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [serviceTypeFilter, setServiceTypeFilter] = useState<string>(() => searchParams.get('service_type') || '');
  /** Default: active only (matches report expectation). Empty = all statuses. */
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState<SortBy>('account');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [planNameMap, setPlanNameMap] = useState<Record<string, string>>({});
  const [serviceTypeOptions, setServiceTypeOptions] = useState<{ value: string; label: string }[]>([]);

  const navigate = useNavigate();

  useEffect(() => {
    apiService
      .getOrderProducts({ category: 'internet', subcategory: 'nbn', type: 'internet_plan' })
      .then((products: { id: string; name: string }[]) => {
        const map: Record<string, string> = {};
        products.forEach((p) => { map[String(p.id)] = p.name; });
        setPlanNameMap(map);
      })
      .catch(() => {});

    apiService
      .getServiceTypes()
      .then((types: { code: string; name: string }[]) => {
        setServiceTypeOptions([
          { value: '', label: 'All types' },
          ...types.map((t) => ({ value: t.code, label: t.name })),
        ]);
      })
      .catch(() => {});
  }, []);

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        page: page + 1,
        per_page: rowsPerPage,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      if (searchQuery.trim()) params.q = searchQuery.trim();
      if (serviceTypeFilter) params.service_type = serviceTypeFilter;
      if (statusFilter) params.status = statusFilter;
      const res = await apiService.searchServices(params);
      setServices(res.services || []);
      setTotal(res.total || 0);
    } catch (err: any) {
      console.error('Failed to fetch services:', err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, sortBy, sortOrder, searchQuery, serviceTypeFilter, statusFilter]);

  useEffect(() => {
    if (hasSearched) fetchServices();
  }, [hasSearched, fetchServices]);

  // Auto-search when arriving with ?service_type= query param (e.g. from dashboard widget).
  useEffect(() => {
    const presetType = searchParams.get('service_type');
    if (presetType) {
      setServiceTypeFilter(presetType);
      setHasSearched(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    setHasSearched(true);
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setServiceTypeFilter('');
    setStatusFilter('active');
    setHasSearched(false);
    setPage(0);
    setSortBy('account');
    setSortOrder('asc');
    setServices([]);
    setTotal(0);
  };

  const handleSort = (column: SortBy) => {
    if (sortBy === column) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setPage(0);
  };

  const SortIcon = ({ column }: { column: SortBy }) =>
    sortBy !== column ? null : sortOrder === 'asc' ? (
      <ArrowUpIcon sx={{ fontSize: 16, ml: 0.25, verticalAlign: 'middle' }} />
    ) : (
      <ArrowDownIcon sx={{ fontSize: 16, ml: 0.25, verticalAlign: 'middle' }} />
    );

  const handleAccountClick = (accountId: string) => {
    navigate(`/accounts/${accountId}`);
  };

  const handleServiceClick = (service: AccountService) => {
    navigate(`/accounts/${service.account_id}`, { state: { openServiceId: service.id } });
  };

  const supaSpeedPlanLabels: Record<string, string> = {
    'supa-25-10': '25 / 10 Mbps',
    'supa-25-25': '25 / 25 Mbps',
    'supa-50-20': '50 / 20 Mbps',
    'supa-50-50': '50 / 50 Mbps',
    'supa-100-20': '100 / 20 Mbps',
    'supa-250-25': '250 / 25 Mbps',
    'supa-500-500': '500 / 500 Mbps',
    'supa-750-750': '750 / 750 Mbps',
    'supa-1000-1000': '1000 / 1000 Mbps',
    'supa-2000-2000': '2000 / 2000 Mbps',
    'supa-3000-3000': '3000 / 3000 Mbps',
  };

  const getPlanDisplay = (row: AccountService) => {
    if (row.service_type === 'supa_networks') {
      const val = (row.type_specific_data as any)?.speed_plan;
      if (!val) return '—';
      return supaSpeedPlanLabels[val] ?? val;
    }
    if (row.service_type !== 'nbn' || !row.nbn_plan_id) return '—';
    return row.nbn_plan_name ?? planNameMap[String(row.nbn_plan_id)] ?? row.nbn_plan_id;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Services report
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        List services by type. Defaults to <strong>Active</strong> status; change Status to include inactive, suspended, or all. Click Account to view the account, or Service ID to view the service detail.
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            label="Search"
            placeholder="Service ID or Account"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 220 }}
          />
          <TextField
            select
            size="small"
            label="Service Type"
            value={serviceTypeFilter}
            onChange={(e) => setServiceTypeFilter(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            {serviceTypeOptions.map((opt) => (
              <MenuItem key={opt.value || 'all'} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            {SERVICE_STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value || 'all-status'} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch}>
            Search
          </Button>
          {hasSearched && (
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => fetchServices()}>
              Refresh
            </Button>
          )}
          {(searchQuery || serviceTypeFilter || statusFilter !== 'active' || hasSearched) && (
            <Button variant="outlined" startIcon={<ClearIcon />} onClick={handleClearFilters}>
              Clear filters
            </Button>
          )}
        </Box>
      </Paper>

      <Paper sx={{ overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('account')}
                    >
                      Account
                      <SortIcon column="account" />
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('service_id')}
                    >
                      Service ID
                      <SortIcon column="service_id" />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Plan
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('service_type')}
                    >
                      Service Type
                      <SortIcon column="service_type" />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    {serviceTypeFilter === 'supa_networks' && (
                      <TableCell sx={{ fontWeight: 600 }}>Connection</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!hasSearched ? (
                    <TableRow>
                      <TableCell colSpan={serviceTypeFilter === 'supa_networks' ? 6 : 5} align="center" sx={{ py: 4 }} color="text.secondary">
                        Enter search or select a filter and click Search to see results.
                      </TableCell>
                    </TableRow>
                  ) : services.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={serviceTypeFilter === 'supa_networks' ? 6 : 5} align="center" sx={{ py: 4 }} color="text.secondary">
                        No services found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    services.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          <Link
                            component="button"
                            variant="body2"
                            onClick={() => handleAccountClick(row.account_id)}
                            sx={{ cursor: 'pointer', fontWeight: 500, textAlign: 'left' }}
                          >
                            {row.account_display_name || row.account_id}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Link
                            component="button"
                            variant="body2"
                            onClick={() => handleServiceClick(row)}
                            sx={{ cursor: 'pointer', fontWeight: 500, textAlign: 'left' }}
                          >
                            {row.service_id}
                          </Link>
                        </TableCell>
                        <TableCell>{getPlanDisplay(row)}</TableCell>
                        <TableCell>
                          <Chip
                            label={
                              (row.service_type_name && String(row.service_type_name).trim()) ||
                              row.service_type.toUpperCase()
                            }
                            size="small"
                            color={getServiceTypeColor(row.service_type) as any}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={(row.status || 'active').replace(/^./, (c) => c.toUpperCase())}
                            size="small"
                            color={
                              row.status === 'active'
                                ? 'success'
                                : row.status === 'suspended'
                                  ? 'warning'
                                  : 'default'
                            }
                            variant="outlined"
                          />
                        </TableCell>
                        {serviceTypeFilter === 'supa_networks' && (
                          <TableCell>
                            <SessionStatusIcon status={String((row.type_specific_data as any)?.session_status || '')} />
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {hasSearched && (
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            )}
          </>
        )}
      </Paper>

    </Box>
  );
};

export default ServicesReportPage;
